import $ from "jquery";
import * as _ from "underscore";

const handlers = {}; // hash of <PAGE>_<KEYCODE>(_<MODIFIER>) to handler functions
const keyDownHandlers = {};

export const keyCodes = {
  backspace: 8,
  enter: 13,
  escape: 27,
  space: 32,
  pageUp: 33,
  pageDown: 34,
  left: 37,
  right: 39,
  up: 38,
  down: 40,
};

// Modern key identifiers using event.key (layout-aware, preferred over keyCodes)
export const keys = {
  forwardSlash: "/",
};

// codes that don't respond to keyUp
const keyDownCodes = [
  8, // backspace
  27, // escape
  33,
  34, // pageUp, pageDown
  37,
  38,
  39,
  40, // cursor keys
];

const modifierKeys = ["ctrl", "alt", "meta"];

type ModifierKey = (typeof modifierKeys)[number];

const buildHandleId = (
  page: string,
  keyIdentifier: number | string,
  modifierSuffix: string
) => {
  return page + "_" + keyIdentifier + modifierSuffix;
};

const handle = (event, keyDown) => {
  const page = $("body").attr("id") || "";

  let modifierSuffix = "";
  _.every(modifierKeys, (key) => {
    if (event[key + "Key"]) {
      modifierSuffix = "_" + key;
      return false;
    }
    return true;
  });

  // Try event.key (modern, layout-aware) first, then fall back to event.which (legacy keyCode)
  const candidateIds = [
    buildHandleId(page, event.key, modifierSuffix),
    buildHandleId(page, event.which, modifierSuffix),
  ];
  const wildcardIds = [
    buildHandleId("*", event.key, ""),
    buildHandleId("*", event.which, ""),
  ];

  const handlerMap = keyDown ? keyDownHandlers : handlers;
  let handler;
  for (const id of candidateIds) {
    handler = handlerMap[id];
    if (handler) break;
  }
  if (!handler) {
    for (const id of wildcardIds) {
      handler = handlers[id];
      if (handler) break;
    }
  }
  if (!handler) {
    return;
  }

  if (!handler.worksEverywhere) {
    // don't grab cursor key events from these elements
    if ($(event.target).closest("input, textarea").length > 0) {
      return;
    }
  }

  handler.handler(event);
};

export const registerHandler = (
  page: string,
  keyCode: number | string,
  handler: (event?: Event, keydown?: boolean) => void,
  worksEverywhere?: boolean /* works in input/textarea */,
  keyDown?: boolean,
  modifierKey?: ModifierKey
) => {
  let handleId = page + "_" + keyCode;

  if (modifierKey) {
    handleId += "_" + modifierKey;
  }

  if (
    keyDown ||
    (typeof keyCode === "number" && keyDownCodes.indexOf(keyCode) !== -1)
  ) {
    keyDownHandlers[handleId] = {
      handler: handler,
      worksEverywhere: worksEverywhere,
    };
  } else {
    handlers[handleId] = {
      handler: handler,
      worksEverywhere: worksEverywhere,
    };
  }
};

export const unregisterHandler = (
  page: string,
  keyCode: number | string,
  keyDown?: boolean,
  modifierKey?: ModifierKey
) => {
  const handleId =
    page + "_" + keyCode + (modifierKey ? "_" + modifierKey : "");

  if (keyDown) {
    delete keyDownHandlers[handleId];
  } else {
    delete handlers[handleId];
  }
};

$(document).ready(() => {
  // keypress is not triggered if the event is captured in a child element, e.g. in an <input>
  $(document).keypress((event) => {
    handle(event, false);
  });

  // keydown is triggered on window.document even when an <input> element has focus
  $(document).keydown((event) => {
    handle(event, true);
  });
});
